import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { FaAngleDown, FaAngleUp, FaBell, FaPen, FaRegBellSlash } from 'react-icons/fa';
import { ReactComponent as TrashIcon } from 'assets/svg/trash_no_color.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import Logger from 'common/services/Logger';
import Events from 'common/services/Events';
import { LOGGER_LOG_TYPE } from 'Config';
import colors from 'styles/export/colors.module.scss';
import VehicleExpensePlafondsService from 'api/vehicleExpensePlafonds/VehicleExpensePlafondsService';
import { PlafondTypeKind, VehicleExpensePlafondDto } from 'api/vehicleExpensePlafonds/models/VehicleExpensePlafondDto';
import ExpensePlafondModal from './ExpensePlafondModal';
import ReplicatePlafondsModal from './ReplicatePlafondsModal';
import styles from './ExpensePlafondsSection.module.scss';

type Props = {
    vehicleId: string;
    isDetails: boolean;
};

/**
 * Task #66600 — plafonds mensais de despesa do ativo.
 *
 * Vive no detalhe do ativo como área colapsável, no molde do "Histórico do ativo" logo acima. Ao
 * contrário dos campos do ativo, é um CRUD próprio com endpoints próprios: o submit do veículo
 * (multipart, com fotografias) fica intacto.
 */
const ExpensePlafondsSection: React.FC<Props> = ({ vehicleId, isDetails }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [plafonds, setPlafonds] = useState<VehicleExpensePlafondDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [editing, setEditing] = useState<VehicleExpensePlafondDto | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showReplicate, setShowReplicate] = useState<boolean>(false);
    const [toRemove, setToRemove] = useState<VehicleExpensePlafondDto | null>(null);

    useEffect(() => {
        if (vehicleId) {
            loadPlafonds();
        }
    }, [vehicleId]);

    /** Recarrega e avisa quem mostra consumo vs plafond (widget, relatório). */
    const reloadAndNotify = async () => {
        await loadPlafonds();
        Events.emit('plafondsChanged', vehicleId);
    };

    const loadPlafonds = async () => {
        try {
            setIsLoading(true);
            const result = await VehicleExpensePlafondsService.getByVehicle(vehicleId);
            setPlafonds(result || []);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load the vehicle expense plafonds', error);
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const onRemoveConfirmed = async () => {
        if (!toRemove?.id) { return; }
        try {
            await VehicleExpensePlafondsService.remove(toRemove.id);
            addToast(t('common.messages.record_delete_success') as string, { appearance: 'success' });
            setToRemove(null);
            reloadAndNotify();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t remove the vehicle expense plafond', error);
            addToast(t('common.messages.record_delete_error') as string, { appearance: 'error' });
            setToRemove(null);
        }
    };

    const openNew = (e: React.MouseEvent) => {
        // A área é clicável para expandir; sem isto o clique no botão colapsava-a a seguir.
        e.stopPropagation();
        setEditing(null);
        setShowModal(true);
        setIsExpanded(true);
    };

    const openReplicate = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowReplicate(true);
    };

    // Os tipos de sistema chegam com o nome interno (FUEL, CLEANING…) e traduzem-se por
    // common.generic_types; os criados pela empresa já vêm com o nome dado pelo utilizador.
    const typeLabel = (plafond: VehicleExpensePlafondDto) => plafond.types
        .map(x => {
            if (x.kind === PlafondTypeKind.MAINTENANCE) { return t('vehicle.plafonds.maintenances') as string; }
            if (!x.typeName) { return ''; }
            return x.isSystem ? (t(`common.generic_types.${x.typeName}` as any) as string) : x.typeName;
        })
        .filter(x => !!x)
        .join(' + ');

    return (
        <>
            <div className={styles.container}>
                {/* Mesmo cabeçalho dos Combustíveis e da Informação técnica: barra cinzenta, título,
                    seta de colapso e o "+" à direita. */}
                <div className={styles.header}>
                    <div className={styles.headerText} onClick={() => setIsExpanded(!isExpanded)}>
                        {t('vehicle.plafonds.title') as string}
                        {plafonds.length > 0 && <span className={styles.counter}>{plafonds.length}</span>}
                    </div>
                    <div className={styles.headerActions}>
                        {!isDetails && plafonds.length > 0 && <span className={styles.linkButton} onClick={openReplicate}>
                            {t('vehicle.plafonds.replicate') as string}
                        </span>}
                        <span className={styles.collapseButton} onClick={() => setIsExpanded(!isExpanded)}>
                            {isExpanded ? <FaAngleUp color={colors.gray} /> : <FaAngleDown color={colors.gray} />}
                        </span>
                        {!isDetails && <span className={styles.headerButton} onClick={openNew}>
                            <img src={AddIcon} className={styles.headerButtonIcon} alt="" />
                        </span>}
                    </div>
                </div>

                {isExpanded && <>
                    <div className={styles.hint}>{t('vehicle.plafonds.hint') as string}</div>

                    {!isLoading && plafonds.length === 0 && <div className={styles.noInfoText}>
                        {t('vehicle.plafonds.empty') as string}
                    </div>}

                    {plafonds.map(plafond => (
                        <div key={plafond.id} className={styles.item}>
                            <div className={styles.itemMain}>
                                <div className={styles.itemName}>{plafond.designation}</div>
                                <div className={styles.itemTypes}>{typeLabel(plafond)}</div>
                            </div>

                            <div className={styles.itemLimit}>
                                <MoneyFormat value={plafond.monthlyLimit} />
                                <span className={styles.perMonth}>{t('vehicle.plafonds.per_month') as string}</span>
                            </div>

                            <div className={styles.itemNotify}>
                                {plafond.notifyEnabled
                                    ? <span className={styles.notifyOn}>
                                        <FaBell />
                                        <span className={styles.notifyText}>{plafond.contacts.length}</span>
                                    </span>
                                    : <FaRegBellSlash className={styles.notifyOff} />}
                            </div>

                            {!isDetails && <div className={styles.itemActions}>
                                <FaPen
                                    className={styles.editIcon}
                                    onClick={() => { setEditing(plafond); setShowModal(true); }}
                                />
                                <TrashIcon className={styles.removeIcon} onClick={() => setToRemove(plafond)} />
                            </div>}
                        </div>
                    ))}
                </>}
            </div>

            <ExpensePlafondModal
                isVisible={showModal}
                vehicleId={vehicleId}
                plafond={editing}
                onClose={() => setShowModal(false)}
                onSaved={() => { setShowModal(false); reloadAndNotify(); }}
            />

            <ReplicatePlafondsModal
                isVisible={showReplicate}
                sourceVehicleId={vehicleId}
                onReplicated={() => Events.emit('plafondsChanged', vehicleId)}
                onClose={() => setShowReplicate(false)}
            />

            <QuestionYesNo
                message={t('common.messages.remove_record') as string}
                isVisible={!!toRemove}
                onYes={onRemoveConfirmed}
                onNo={() => setToRemove(null)}
            />
        </>
    );
};

export default ExpensePlafondsSection;
